import React, { useState } from 'react'
import { Button, Group, Select, Stack, Text } from '@mantine/core'

import { EventFeedbackForm } from './EventFeedbackForm'
import { useFeedbackForm } from './useFeedbackForm'

import { Card } from '@/components/Event/Detail/components/Card'
import { StateSelect } from '@/components/StateSelect'
import { Event } from '@/types'
import { isWithClientReporter } from '@/utils/parrot'
import { COUNTIES, STATES_CODES } from '@/utils/states'

// Presentational CR feedback form. The form instance is owned by the caller (so the
// CR Form can share one form across its steps and this view); `onSubmitWithErrors`
// is injectable so the staged CR Form can also open invalid steps on a failed submit.
export const FeedbackFormView: React.FC<
  { event: Event; staged?: boolean } & ReturnType<typeof useFeedbackForm>
> = ({
  event,
  form,
  save,
  saving,
  submit,
  onSubmitWithErrors,
  staged = false,
}) => {
  const isHearing = event.type === 'hearing'

  const shouldProvideLocation = !isHearing && !isWithClientReporter(event)

  const [activeWitnessIndex, setActiveWitnessIndex] = useState(0)

  // Surface the "all sections must be Yes" gate above the submit button, recomputed live
  // so it clears as sections get checked (no scrolling to / expanding the unchecked ones).
  const allStepsDone = Object.values(form.values.steps ?? {}).every(
    step => step.done,
  )
  const showStepsError = staged && !!form.errors['steps'] && !allStepsDone

  return (
    <form
      onSubmit={form.onSubmit(submit, onSubmitWithErrors)}
      noValidate // disable html5 validation so JS validation runs immediately
    >
      <Stack>
        {/* In staged mode all per-witness capture lives in the CR Form steps above,
            so the witness form isn't rendered here — only location + submit remain. */}
        {!staged && (
          <EventFeedbackForm
            form={form}
            event={event}
            activeWitnessIndex={activeWitnessIndex}
            setActiveWitnessIndex={setActiveWitnessIndex}
          />
        )}
        {shouldProvideLocation && (
          <Card heading="Where were you located during event">
            <Stack>
              <StateSelect
                label="State"
                placeholder="Pick one"
                error={form.errors.reporter_state}
                value={form.values.reporter_state}
                onChange={value => {
                  form.setFieldValue('reporter_state', value ?? '')
                  form.setFieldValue('reporter_county', '')
                }}
                states={STATES_CODES}
                required
              />

              <Select
                required
                label="County"
                placeholder="Pick one"
                error={form.errors.reporter_county}
                value={form.values.reporter_county}
                onChange={value =>
                  form.setFieldValue('reporter_county', value ?? '')
                }
                data={
                  form.values.reporter_state
                    ? COUNTIES[form.values.reporter_state].map(county => ({
                        value: county,
                        label: county,
                      }))
                    : []
                }
                disabled={!form.values.reporter_state}
                searchable
              />
            </Stack>
          </Card>
        )}

        {showStepsError && (
          <Text color="red" size="sm" align="right">
            {form.errors['steps']}
          </Text>
        )}

        <Group position="right">
          <Button
            loading={saving}
            color="gray"
            variant="light"
            className="w-48"
            onClick={save}
          >
            Save changes
          </Button>

          {!staged &&
          activeWitnessIndex + 1 !== form.values.events_feedbacks?.length ? (
            <Button
              type="button"
              className="min-w-48"
              onClick={async event => {
                event.stopPropagation()

                await save()
                setActiveWitnessIndex(activeWitnessIndex + 1)
              }}
            >
              Submit and continue to witness {activeWitnessIndex + 2}
            </Button>
          ) : (
            <Button
              type="submit"
              loading={form.submitting}
              className="min-w-48"
            >
              Submit form
            </Button>
          )}
        </Group>
      </Stack>
    </form>
  )
}
